import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Stack, FormControl, InputLabel, Select, MenuItem,
  Grid, Typography, Alert, Box, Tabs, Tab, Avatar, Divider,
  InputAdornment, IconButton, CircularProgress,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import LinkIcon from "@mui/icons-material/Link";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import BusinessIcon from "@mui/icons-material/Business";
import EventIcon from "@mui/icons-material/Event";
import GroupIcon from "@mui/icons-material/Group";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SearchIcon from "@mui/icons-material/Search";
import CelebrationIcon from "@mui/icons-material/Celebration";
import CloseIcon from "@mui/icons-material/Close";
import {
  convertEmailToLead, linkEmailToLead, fetchLeads,
  type LeadThread,
} from "../LeadSystem/leadApi";
import { fetchLeadPrefill } from "./emailHubV3Api";
import PremiumDatePicker from "../../Components/PremiumDatePicker";

const NAVY = "#1a3353";
const GOLD = "#D4AF37";

const EVENT_TYPES = [
  { value: "wedding", label: "Wedding", icon: "💒" },
  { value: "corporate", label: "Corporate Event", icon: "🏢" },
  { value: "birthday", label: "Birthday Party", icon: "🎂" },
  { value: "engagement", label: "Engagement", icon: "💍" },
  { value: "mehndi", label: "Mehndi", icon: "🎨" },
  { value: "charity_gala", label: "Charity Gala", icon: "🎗" },
  { value: "convocation", label: "Convocation", icon: "🎓" },
  { value: "baby_shower", label: "Baby Shower", icon: "👶" },
  { value: "anniversary", label: "Anniversary", icon: "🥂" },
  { value: "retirement", label: "Retirement", icon: "🎉" },
  { value: "holiday_party", label: "Holiday Party", icon: "🎄" },
  { value: "other", label: "Other", icon: "📋" },
];

interface Props {
  open: boolean;
  onClose: () => void;
  onConverted: () => void;
  emailThreadId: number;
  prefillName?: string;
  prefillEmail?: string;
  aiData?: {
    event_type?: string | null;
    event_date?: string | null;
    guest_count?: number | null;
    budget?: string | null;
    phone?: string | null;
    organization?: string | null;
  } | null;
}

export default function ConvertToLeadModal({
  open, onClose, onConverted,
  emailThreadId, prefillName, prefillEmail, aiData,
}: Props) {
  const [tab, setTab] = useState(0);
  const [existingLeads, setExistingLeads] = useState<LeadThread[]>([]);
  const [selectedLeadId, setSelectedLeadId] = useState<number | "">("");
  const [leadSearch, setLeadSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", organization: "",
    event_type: "", event_date: "", guest_count: "", budget_value: "",
  });

  useEffect(() => {
    if (open) {
      setTab(0);
      setSelectedLeadId("");
      setLeadSearch("");
      setError(null);
      setForm({ name: prefillName || "", email: prefillEmail || "", phone: "", organization: "", event_type: "", event_date: "", guest_count: "", budget_value: "" });
      fetchLeads({ per_page: 50 }).then(setExistingLeads).catch(() => {});

      // Fetch AI-extracted prefill data from backend
      setPrefillLoading(true);
      fetchLeadPrefill(emailThreadId)
        .then((data) => {
          setForm({
            name: data.customer_name || prefillName || "",
            email: data.email || prefillEmail || "",
            phone: data.phone || "",
            organization: data.organization || "",
            event_type: data.event_type || "",
            event_date: data.event_date || "",
            guest_count: data.guest_count ? String(data.guest_count) : "",
            budget_value: data.budget ? String(data.budget).replace(/[^0-9.]/g, "") : "",
          });
        })
        .catch(() => {})
        .finally(() => setPrefillLoading(false));
    }
  }, [open, emailThreadId, prefillName, prefillEmail]);

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((p) => ({ ...p, [field]: e.target.value }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      if (tab === 0) {
        await convertEmailToLead({
          email_thread_id: emailThreadId,
          name: form.name,
          email: form.email || undefined,
          phone: form.phone || undefined,
          organization: form.organization || undefined,
          event_type: form.event_type || undefined,
          event_date: form.event_date || undefined,
          guest_count: form.guest_count ? parseInt(form.guest_count) : undefined,
          budget_value: form.budget_value ? parseFloat(form.budget_value) : undefined,
        });
      } else if (selectedLeadId) {
        await linkEmailToLead(emailThreadId, Number(selectedLeadId));
      }
      onConverted();
    } catch (err: any) {
      // Laravel returns { message: "...", errors: { field: [...] } } on validation failure
      // or { message: "..." } on server error
      const firstValidationError = err?.errors
        ? Object.values(err.errors as Record<string, string[]>).flat()[0]
        : null;
      const msg = firstValidationError || err?.message || "Failed to convert. Please try again.";
      setError(String(msg));
    }
    setSaving(false);
  };

  const filteredLeads = existingLeads.filter((l) =>
    l.name.toLowerCase().includes(leadSearch.toLowerCase()) ||
    (l.email || "").toLowerCase().includes(leadSearch.toLowerCase()) ||
    (l.organization || "").toLowerCase().includes(leadSearch.toLowerCase())
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      {/* Header */}
      <Box sx={{ bgcolor: NAVY, color: "#fff", px: 3, py: 2.5, position: "relative" }}>
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Avatar sx={{ bgcolor: GOLD, width: 40, height: 40 }}>
            <CelebrationIcon sx={{ color: NAVY, fontSize: 22 }} />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>Link to Lead System</Typography>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>Convert email inquiry into a trackable lead</Typography>
          </Box>
        </Stack>
        <IconButton onClick={onClose} sx={{ position: "absolute", top: 12, right: 12, color: "rgba(255,255,255,0.7)", "&:hover": { color: "#fff" } }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          px: 3, bgcolor: "#f8f9fa", minHeight: 48,
          "& .MuiTab-root": { minHeight: 48, fontWeight: 600, fontSize: 13, textTransform: "none" },
          "& .Mui-selected": { color: `${NAVY} !important` },
          "& .MuiTabs-indicator": { bgcolor: GOLD, height: 3 },
        }}
      >
        <Tab icon={<PersonAddIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Create New Lead" />
        <Tab icon={<LinkIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Link to Existing" />
      </Tabs>

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {prefillLoading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, color: "#64748b" }}>
            <CircularProgress size={14} thickness={5} />
            <Typography variant="caption">Extracting details from email...</Typography>
          </Box>
        )}
        {tab === 0 ? (
          <Stack spacing={2.5}>
            {/* Contact Info Section */}
            <Box>
              <Typography variant="overline" sx={{ color: NAVY, fontWeight: 700, letterSpacing: 1, fontSize: 11 }}>
                Contact Information
              </Typography>
              <Divider sx={{ mt: 0.5, mb: 1.5 }} />
              <TextField
                label="Full Name"
                fullWidth
                required
                value={form.name}
                onChange={handleChange("name")}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><PersonIcon sx={{ color: "#94a3b8", fontSize: 20 }} /></InputAdornment>,
                }}
                sx={{ mb: 1.5 }}
              />
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <TextField
                    label="Email"
                    fullWidth
                    value={form.email}
                    onChange={handleChange("email")}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><EmailIcon sx={{ color: "#94a3b8", fontSize: 20 }} /></InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Phone"
                    fullWidth
                    value={form.phone}
                    onChange={handleChange("phone")}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ color: "#94a3b8", fontSize: 20 }} /></InputAdornment>,
                    }}
                  />
                </Grid>
              </Grid>
              <TextField
                label="Organization"
                fullWidth
                value={form.organization}
                onChange={handleChange("organization")}
                sx={{ mt: 1.5 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><BusinessIcon sx={{ color: "#94a3b8", fontSize: 20 }} /></InputAdornment>,
                }}
              />
            </Box>

            {/* Event Details Section */}
            <Box>
              <Typography variant="overline" sx={{ color: NAVY, fontWeight: 700, letterSpacing: 1, fontSize: 11 }}>
                Event Details
              </Typography>
              <Divider sx={{ mt: 0.5, mb: 1.5 }} />
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Event Type</InputLabel>
                    <Select
                      value={form.event_type}
                      label="Event Type"
                      onChange={(e) => setForm((p) => ({ ...p, event_type: e.target.value }))}
                      startAdornment={<InputAdornment position="start"><EventIcon sx={{ color: "#94a3b8", fontSize: 20 }} /></InputAdornment>}
                    >
                      {EVENT_TYPES.map((t) => (
                        <MenuItem key={t.value} value={t.value}>
                          <Stack direction="row" alignItems="center" gap={1}>
                            <span>{t.icon}</span>
                            <span>{t.label}</span>
                          </Stack>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <PremiumDatePicker
                    label="Event Date"
                    value={form.event_date}
                    onChange={(v) => setForm((p) => ({ ...p, event_date: v }))}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Guest Count"
                    type="number"
                    fullWidth
                    value={form.guest_count}
                    onChange={handleChange("guest_count")}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><GroupIcon sx={{ color: "#94a3b8", fontSize: 20 }} /></InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Budget"
                    type="number"
                    fullWidth
                    value={form.budget_value}
                    onChange={handleChange("budget_value")}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><AttachMoneyIcon sx={{ color: "#94a3b8", fontSize: 20 }} /></InputAdornment>,
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Stack>
        ) : (
          <Box>
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2, bgcolor: "#f0f7ff", border: "1px solid #bfdbfe" }}>
              Select an existing lead to link this email conversation to
            </Alert>
            <TextField
              placeholder="Search leads by name, email, or organization..."
              fullWidth
              value={leadSearch}
              onChange={(e) => setLeadSearch(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#94a3b8" }} /></InputAdornment>,
              }}
            />
            <Box sx={{ maxHeight: 280, overflow: "auto", border: "1px solid #e2e8f0", borderRadius: 2 }}>
              {filteredLeads.length === 0 ? (
                <Box sx={{ p: 3, textAlign: "center", color: "#94a3b8" }}>
                  <Typography variant="body2">No leads found</Typography>
                </Box>
              ) : (
                filteredLeads.map((lead) => {
                  const isSelected = selectedLeadId === lead.id;
                  const initials = lead.name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
                  return (
                    <Box
                      key={lead.id}
                      onClick={() => setSelectedLeadId(lead.id)}
                      sx={{
                        px: 2, py: 1.5,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        borderBottom: "1px solid #f1f5f9",
                        bgcolor: isSelected ? `${GOLD}15` : "transparent",
                        borderLeft: isSelected ? `3px solid ${GOLD}` : "3px solid transparent",
                        "&:hover": { bgcolor: isSelected ? `${GOLD}15` : "#f8fafc" },
                        transition: "all 0.15s",
                      }}
                    >
                      <Avatar sx={{ width: 36, height: 36, bgcolor: isSelected ? GOLD : "#e2e8f0", color: isSelected ? NAVY : "#64748b", fontSize: 13, fontWeight: 700 }}>
                        {initials}
                      </Avatar>
                      <Box flex={1}>
                        <Typography variant="body2" fontWeight={600} color={NAVY}>{lead.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {[lead.email, lead.event_type?.replace(/_/g, " "), lead.organization].filter(Boolean).join(" \u00B7 ")}
                        </Typography>
                      </Box>
                      {isSelected && (
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: GOLD }} />
                      )}
                    </Box>
                  );
                })
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 2, bgcolor: "#f8f9fa" }}>
        <Button onClick={onClose} sx={{ color: "#64748b", fontWeight: 600 }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || (tab === 0 && !form.name.trim()) || (tab === 1 && !selectedLeadId)}
          startIcon={tab === 0 ? <PersonAddIcon /> : <LinkIcon />}
          sx={{
            bgcolor: NAVY,
            fontWeight: 700,
            px: 3,
            borderRadius: 2,
            "&:hover": { bgcolor: "#2d5f8a" },
            "&:disabled": { bgcolor: "#cbd5e1" },
          }}
        >
          {saving ? "Processing..." : tab === 0 ? "Convert to Lead" : "Link to Lead"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
